//@ts-check
import * as d3 from "d3";

/**
 * Apply custom color on the single element
 * @param {string} group_ID Pass the element id from the loaded drawing in 2D Viewer
 * @param {Number} color Pass the Hex Color Code to applied on single element
 * @returns {Object} It returns an object for acknowledgement of this API to the consumer side.
 */
export function colorElement(group_ID, color) {
  let scope = this;

  let group = scope.content.selectAll('#' + group_ID);

  if (group._groups && group._groups.length != 0) {
    try {
      if (group.attr('status') != 'locked') {
        // Store the current filter state if it exists
        const isFiltered = group.attr('data-filtered') === 'true';
        const opacity = group.attr('data-opacity');
        
        // Apply the color
        group.attr('data-isCol', true);
        group.attr('data-color', color);
        scope.colorGroup(group, color);
        
        // Re-apply opacity if it was filtered
        if (isFiltered && opacity) {
          group.selectAll(scope.types).style('opacity', opacity);
        }
        
        scope.arrayElements.push(group_ID);
        scope.temp_colorBy.push(group);
      }
      else {
        //console.log('Error : ['+String(group_ID)+'] Invalid element id');
      }
    } catch (e) {
      //console.log("Error: Coloring Element:", group_ID, model);
      var returnValue = {
        "status": "failed",
        "message": 'System Error: ' + e.message
      }
      return returnValue;
    }
  } else {
    var returnValue = {
      "status": "failed",
      "message": "Error : Group ID not found"
    }
    return returnValue;
  }
}

/**
* Apply the custom colors on multiple elements
* @param {Array<string>} group_ID Pass the array of element id from the loaded drawing in 2D Viewer
* @param {Array<Number>} colors Pass the array of Hex Color Code to apply on multiple elements
* @param {boolean} hide If passed true then hides all filtered elements. If passed false the apply ghost color on the filtered elements
* @returns {Object} It returns an object for acknowledgement of this API to the consumer side.
*/
export function colorElements(group_ID, colors, hide) {
  let scope = this;

  if (scope.content._groups && scope.content._groups.length != 0) {
    // Apply colors to each element
    for (let i = 0; i < group_ID.length; i++) {
      // Only isolate if hide is true (otherwise we'll just apply colors)
      if (hide) {
        scope.isolateElement(group_ID[i]);
      }
      
      // Apply color
      scope.colorElement(group_ID[i], colors[i]);
    }
    
    // Mark elements that should use ghosting effect
    if (!hide && scope.isGhostColor) {
      // Find elements not in our coloring list
      scope.content.selectAll('g').each(function() {
        const elem = d3.select(this);
        const id = elem.attr('id');
        
        if (id && !group_ID.includes(id) && !elem.attr('data-isCol')) {
          // Apply ghost color only if not already colored
          scope.colorGroup(elem, scope.ghostColor);
          elem.attr('data-ghosted', true);
          scope.groupOpacity(elem, scope.filterOpacity);
        }
      });
    }
    
    var returnValue = {
      "status": "success",
      "message": "Colors all elements"
    };
    return returnValue;
  } 
  
  return {
    "status": "failed",
    "message": 'Error: Drawing is not loaded in viewer.'
  };
}

/**
 * Clears group style
 * @param {*} group - Group
 */
export function clearGroupStyle(group) {
  let scope = this;

  // Add null check before trying to use the group
  if (!group || !group.node()) {
    return;
  }

  let elements = group.selectAll(scope.types);
  if (elements._groups && elements._groups.length != 0) scope.clearStyle(elements);
}

/**
 * 
 * @param {*} element - Element
 */
export function clearStyle(element) {
  element.each(function () {
    this.style.fill = null;
    this.style.stroke = null;
    this.style.opacity = null;
  });
}

/**
  * Reset the single element
  * @param {any} group_ID Pass the element id from the loaded drawing in 2D Viewer
  * @returns {Object} It returns an object for acknowledgement of this API to the consumer side.
  */
export function resetElement(group_ID) {
  let scope = this;
  if (scope.content.children.length != 0) {
    try {
      if (scope.content.selectAll('#' + group_ID).attr('label') == 'attached') {
        d3.select(scope.content).selectAll('#' + group_ID).selectAll('text').remove();
        d3.selectAll('#' + group_ID).attr('label', null);
      }
      if (d3.select(scope.content).selectAll('#' + group_ID).attr('status') == 'locked') {
        d3.select(scope.content).selectAll('#' + group_ID).attr('status', null);
      }
      d3.select(scope.content).selectAll('#' + group_ID).selectAll(scope.types).each(function () {
        scope.clearStyle(this);
      })
      if (scope.isGhostColor) scope.isGhostColor = false;
    }
    catch {
      //console.log('Error : Invalid element id.'); 
    }

    var returnValue = {
      "status": "success",
      "message": "Reset colors of the group/element"
    }
    return returnValue;

  } else { 
    var returnValue = {
      "status": "failed",
      "message": 'Error : Drawing is not loaded in viewer.'
    }
    return returnValue;
  }

}

/**
 * Resets all the applied custom colors from drawing
 * @returns {Object} It returns an object for acknowledgement of this API to the consumer side.
 */
export function resetAllColors() {
  let scope = this;

  scope.temp_colorBy.forEach(group => {
    // Add null check before accessing attributes
    if (!group || !group.node()) {
      // Skip this item if it's null or doesn't have a node
      return;
    }
    
    group.attr('data-isCol', false);
    
    // Preserve filter state if it exists
    const isFiltered = group.attr('data-filtered') === 'true';
    const opacity = group.attr('data-opacity');
    const isGhosted = group.attr('data-ghosted') === 'true';
    
    // Clear styles
    scope.clearGroupStyle(group);
    
    // Re-apply filter effects if they were active
    if (isFiltered && opacity) {
      group.selectAll(scope.types).style('opacity', opacity);
    }
    
    if (isGhosted) {
      scope.colorGroup(group, scope.ghostColor);
    }
  });

  // Clean up the array, removing any null or invalid groups
  scope.temp_colorBy = scope.temp_colorBy.filter(group => group && group.node());
  scope.isGhostColor = false;
  
  return {
    "status": "success",
    "message": "Reset all colors"
  };
}

/**
 * Set Color the element/group
 * @param {*} group - Group ?
 * @param {Number} color - Hex Color
 */
export function colorGroup(group, color) {
  let scope = this;

  // Add null check before trying to use the group
  if (!group || !group.node()) {
    return;
  }

  let elements = group.selectAll(scope.types);

  if (elements._groups && elements._groups.length != 0) scope.applyColor(elements, color);

}

/**
 * 
 * @param {*} elements - Elements
 * @param {Number} color - Hex Color
 */
export function applyColor(elements, color) {
  let scope = this;
  elements.each(function () {
    // if (scope.strokeWidth > 0) this.style.strokeWidth = scope.strokeWidth;
    if (this.getAttribute("data-isFill") == 'true') this.style.fill = color;
    if (this.getAttribute("data-isStroke") == 'true') this.style.stroke = color;
  });
}